/**
 * Batch Sort Selector Component
 * Dropdown to select sort field and toggle sort order
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import type { BatchSort, BatchSortField, SortOrder } from '@/types/availableBatches';
import { SORT_FIELD_OPTIONS } from '@/types/availableBatches';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BatchSortSelectorProps {
  sort: BatchSort;
  onSortChange: (sort: BatchSort) => void;
  className?: string;
}

export function BatchSortSelector({
  sort,
  onSortChange,
  className
}: BatchSortSelectorProps) {
  const handleFieldChange = (field: string) => {
    onSortChange({
      ...sort,
      field: field as BatchSortField
    });
  };

  const toggleOrder = () => {
    onSortChange({
      ...sort,
      order: sort.order === 'ASC' ? 'DESC' : 'ASC'
    });
  };

  const getSortLabel = () => {
    const fieldOption = SORT_FIELD_OPTIONS.find(opt => opt.value === sort.field);
    return fieldOption?.label || 'Sort by';
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Select value={sort.field} onValueChange={handleFieldChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Sort by..." />
        </SelectTrigger>
        <SelectContent>
          {SORT_FIELD_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="outline"
        size="icon"
        onClick={toggleOrder}
        aria-label={`Sort order: ${sort.order === 'ASC' ? 'Ascending' : 'Descending'}`}
        title={`Sort order: ${sort.order === 'ASC' ? 'Ascending' : 'Descending'}`}
      >
        {sort.order === 'ASC' ? (
          <ArrowUp className="h-4 w-4" />
        ) : (
          <ArrowDown className="h-4 w-4" />
        )}
      </Button>

      <span className="text-sm text-gray-600 hidden md:inline">
        {getSortLabel()} ({sort.order === 'ASC' ? 'Oldest First' : 'Newest First'})
      </span>
    </div>
  );
}
